<script>
	import { onMount } from "svelte";
	import { createPixCharge, verifyPixPayment, safeApiFetch, fetchPublicSupport } from "../services/api";
	import Icon from "../components/icons/Icon.svelte";

	export let storeId = "";
	export let storeName = "";
	export let planName = "MataFome Pro";
	export let monthlyPrice = 97.00;
	export let onActivateKey = (key) => {};
	export let onRenewSuccess = () => {};
	export let onLogout = () => {};

	let licenseKeyInput = "";
	let isCheckingPix = false;
	let showPixModal = false;
	let pixCopied = false;
	let keyError = "";
	let currentPaymentData = null;
	let isLoadingPix = false;

	let pixError = "";
	// Chave PIX Dinâmica gerada oficialmente pelo Mercado Pago Master
	let pixPayload = "";
	let pixQrBase64 = "";

	// Suporte SaaS Oficial Dinâmico (Fase C)
	let supportWhatsappUrl = "";
	let isSupportAvailable = false;

	onMount(async () => {
		try {
			const res = await safeApiFetch('/api/saas/status');
			if (res && res.ok) {
				const data = await res.json();
				if (data.monthlyPrice) {
					monthlyPrice = parseFloat(data.monthlyPrice) || 97.00;
				}
				if (data.storeName && !storeName) {
					storeName = data.storeName;
				}
				if (data.plan && (!planName || planName === 'MataFome Pro')) {
					planName = data.plan;
				}
			}
		} catch (e) {
			console.warn("Não foi possível sincronizar valor dinâmico da mensalidade:", e);
		}

		try {
			const support = await fetchPublicSupport();
			if (support && support.whatsapp) {
				const cleanWa = String(support.whatsapp).replace(/\D/g, '');
				if (cleanWa) {
					const text = support.message || 'Olá, gostaria de ajuda para renovar a minha assinatura do MataFome';
					supportWhatsappUrl = `https://wa.me/${cleanWa}?text=${encodeURIComponent(text)}`;
					isSupportAvailable = true;
				}
			}
		} catch (err) {
			console.warn("Não foi possível carregar canais de suporte:", err);
		}
	});

	async function handleOpenPixModal() {
		showPixModal = true;
		isLoadingPix = true;
		pixPayload = "";
		pixQrBase64 = "";
		pixError = "";
		try {
			const res = await createPixCharge(storeId, storeName, monthlyPrice);
			if (res && res.qrCode) {
				currentPaymentData = res;
				pixPayload = res.qrCode;
				pixQrBase64 = res.qrCodeBase64 ? (res.qrCodeBase64.startsWith('data:') ? res.qrCodeBase64 : `data:image/png;base64,${res.qrCodeBase64}`) : "";
			} else {
				pixError = res?.error || "Serviço de pagamento PIX temporariamente indisponível. Entre em contato com o suporte.";
			}
		} catch (e) {
			pixError = e.message || "Serviço de pagamento PIX temporariamente indisponível. Entre em contato com o suporte.";
		} finally {
			isLoadingPix = false;
		}
	}

	function copyPixCode() {
		if (navigator.clipboard && pixPayload) {
			navigator.clipboard.writeText(pixPayload);
			pixCopied = true;
			setTimeout(() => (pixCopied = false), 4000);
		}
	}

	async function handleVerifyPix() {
		if (!currentPaymentData?.paymentId) return;
		isCheckingPix = true;
		pixError = "";
		try {
			const res = await verifyPixPayment(storeId, currentPaymentData.paymentId);
			if (res && (res.success || res.status === 'approved')) {
				showPixModal = false;
				onRenewSuccess();
			} else {
				pixError = res?.error || "Pagamento ainda não foi identificado pela instituição financeira. Aguarde alguns instantes.";
			}
		} catch (e) {
			pixError = e.message || "Pagamento ainda não identificado. Aguarde alguns instantes.";
		} finally {
			isCheckingPix = false;
		}
	}

	let isActivatingKey = false;
	async function handleApplyKey() {
		if (!licenseKeyInput.trim()) {
			keyError = "Por favor, digite a chave de licença fornecida pelo suporte.";
			return;
		}
		keyError = "";
		isActivatingKey = true;
		try {
			const res = await onActivateKey(licenseKeyInput.trim());
			if (res && res.error) {
				keyError = res.error;
			}
		} catch (err) {
			keyError = err.message || "Erro ao validar chave de licença.";
		} finally {
			isActivatingKey = false;
		}
	}
</script>

<div class="paywall-page">
	<div class="paywall-card">
		<!-- Logo Oficial do MataFome (Hero Dark - Branco & Laranja nítido) -->
		<div class="brand-logo-container">
			<img src="./brand/matafome_logo_hero_dark.png" alt="MataFome • Seu pedido, mais feliz" class="brand-logo-img" />
		</div>

		<div class="status-badge-blocked">
			<span class="dot-red"></span>
			<span>Assinatura Vencida ou Pendente</span>
		</div>

		<h1 class="paywall-title">Acesso ao Sistema Pausado</h1>
		<p class="paywall-subtitle">
			O restaurante <strong>{storeName}</strong> precisa regularizar a mensalidade para continuar recebendo pedidos automáticos, operando o robô de WhatsApp e imprimindo na cozinha.
		</p>

		<!-- Card de Detalhes do Plano -->
		<div class="plan-details-box">
			<div class="plan-row">
				<span class="plan-label">Plano Vinculado</span>
				<span class="plan-value">{planName}</span>
			</div>
			<div class="plan-row">
				<span class="plan-label">Valor da Mensalidade</span>
				<span class="plan-price">R$ {parseFloat(monthlyPrice || 97).toFixed(2).replace('.', ',')} <small>/ mês</small></span>
			</div>
			<div class="plan-row">
				<span class="plan-label">Status da Licença</span>
				<span class="status-pill-unpaid">Aguardando Pagamento</span>
			</div>
		</div>

		<!-- Ações de Pagamento -->
		<div class="actions-group">
			<button class="btn-pix-pay" on:click={handleOpenPixModal}>
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
					<rect x="3" y="5" width="18" height="14" rx="2"/>
					<path d="M7 15h10M7 11h10M7 7h4"/>
				</svg>
				<span>Regularizar via PIX (Liberação Automática)</span>
			</button>

			<div class="key-activation-row">
				<input
					type="text"
					placeholder="Já possui chave de liberação? (ex: MATA-PRO-XXXX)"
					bind:value={licenseKeyInput}
					disabled={isActivatingKey}
				/>
				<button class="btn-activate-key" on:click={handleApplyKey} disabled={isActivatingKey}>
					{isActivatingKey ? "Ativando..." : "Ativar Chave"}
				</button>
			</div>
			{#if keyError}
				<p class="error-msg">{keyError}</p>
			{/if}
		</div>

		<!-- Suporte & Logout -->
		<div class="paywall-footer">
			{#if isSupportAvailable && supportWhatsappUrl}
				<a
					href={supportWhatsappUrl}
					target="_blank"
					rel="noopener noreferrer"
					class="link-support"
				>
					<Icon name="message-square" size={15} /> Falar com o Administrador / Suporte
				</a>
			{:else}
				<span class="link-support disabled" title="Canal de suporte temporariamente indisponível" style="opacity: 0.6; cursor: not-allowed; display: inline-flex; align-items: center; gap: 6px;">
					<Icon name="message-square" size={15} /> Suporte temporariamente indisponível
				</span>
			{/if}
			<span class="bullet">•</span>
			<button class="btn-logout" on:click={onLogout}>
				Sair para a tela de Login
			</button>
		</div>
	</div>

	<!-- Modal de Pagamento PIX -->
	{#if showPixModal}
		<div class="pix-modal-overlay" on:click={() => (showPixModal = false)}>
			<div class="pix-modal-content" on:click|stopPropagation>
				<div class="pix-modal-header">
					<h3>Pagamento da Mensalidade via PIX</h3>
					<button class="btn-close-modal" on:click={() => (showPixModal = false)}><Icon name="x" size={16} /></button>
				</div>

				{#if isLoadingPix}
					<div style="padding: 40px 20px; text-align: center; color: #94A3B8;">
						<p>Gerando cobrança oficial PIX...</p>
					</div>
				{:else if pixError || !pixPayload}
					<div style="padding: 24px 20px; text-align: center;">
						<div style="color: #EF4444; font-weight: 700; margin-bottom: 8px;">Pagamento Indisponível</div>
						<p style="font-size: 13.5px; color: #94A3B8; line-height: 1.5; margin: 0 0 16px 0;">
							{pixError || "O serviço de cobrança PIX não está configurado no momento. Por favor, regularize sua assinatura diretamente com o suporte ou utilize uma chave de licença."}
						</p>
					</div>
				{:else}
					<div class="pix-qr-area">
						<div class="qr-placeholder">
							<!-- QR Code Dinâmico oficial retornado pelo gateway com contingência -->
							{#if pixQrBase64}
								<img
									src={pixQrBase64}
									alt="QR Code PIX MataFome"
									class="qr-img"
								/>
							{:else}
								<img
									src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data={encodeURIComponent(pixPayload)}"
									alt="QR Code PIX MataFome"
									class="qr-img"
								/>
							{/if}
						</div>
						<p class="qr-instructions">
							Abra o aplicativo do seu banco, escolha <strong>Pagar com PIX</strong> e aponte a câmera para o QR Code acima.
						</p>
					</div>

					<div class="pix-code-box">
						<label>Código PIX Copia e Cola:</label>
						<div class="copy-input-row">
							<input type="text" readonly value={pixPayload} />
							<button class="btn-copy" on:click={copyPixCode}>
								{#if pixCopied}
									Copiado! <Icon name="check" size={13} />
								{:else}
									Copiar
								{/if}
							</button>
						</div>
					</div>

					<button
						class="btn-confirm-pix"
						on:click={handleVerifyPix}
						disabled={isCheckingPix}
					>
						{isCheckingPix ? "Verificando confirmação do banco..." : "Já realizei o pagamento (Liberar Acesso)"}
					</button>
				{/if}
			</div>
		</div>
	{/if}
</div>

<style>
	.paywall-page {
		width: 100%;
		height: 100%;
		background: radial-gradient(circle at 50% 30%, #1A1F2B 0%, #0A0D14 70%);
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 24px;
		box-sizing: border-box;
		font-family: var(--font-sans);
	}

	.paywall-card {
		background: #11141C;
		border: 1px solid rgba(255, 255, 255, 0.08);
		box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6), 0 0 30px rgba(255, 106, 0, 0.08);
		border-radius: 20px;
		max-width: 520px;
		width: 100%;
		padding: 36px 32px;
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		box-sizing: border-box;
	}

	.brand-logo-container {
		display: flex;
		justify-content: center;
		align-items: center;
		margin: 0 auto 18px auto;
		max-width: 210px;
	}

	.brand-logo-img {
		width: 100%;
		height: auto;
		display: block;
		object-fit: contain;
	}

	.status-badge-blocked {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: rgba(239, 68, 68, 0.12);
		border: 1px solid rgba(239, 68, 68, 0.3);
		color: #EF4444;
		font-size: 11px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.5px;
		padding: 4px 12px;
		border-radius: 9999px;
		margin-bottom: 16px;
	}

	.dot-red {
		width: 7px;
		height: 7px;
		border-radius: 50%;
		background: #EF4444;
		box-shadow: 0 0 6px #EF4444;
	}

	.paywall-title {
		font-size: 24px;
		font-weight: 800;
		color: #FFFFFF;
		margin: 0 0 10px 0;
		letter-spacing: -0.4px;
	}

	.paywall-subtitle {
		font-size: 13px;
		color: #94A3B8;
		line-height: 1.6;
		margin: 0 0 24px 0;
	}

	.plan-details-box {
		width: 100%;
		background: #111113;
		border: 1px solid rgba(255, 255, 255, 0.08);
		border-radius: 10px;
		padding: 16px 20px;
		margin-bottom: 24px;
		display: flex;
		flex-direction: column;
		gap: 12px;
		box-sizing: border-box;
	}

	.plan-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		font-size: 13px;
	}

	.plan-label {
		color: #94A3B8;
		font-weight: 600;
	}

	.plan-value {
		color: #FFFFFF;
		font-weight: 700;
	}

	.plan-price {
		font-size: 16px;
		font-weight: 800;
		color: #FF6A00;
	}

	.plan-price small {
		font-size: 11px;
		font-weight: 500;
		color: #94A3B8;
	}

	.status-pill-unpaid {
		background: rgba(245, 158, 11, 0.15);
		border: 1px solid rgba(245, 158, 11, 0.35);
		color: #F59E0B;
		font-size: 11px;
		font-weight: 700;
		padding: 2px 8px;
		border-radius: 4px;
	}

	.actions-group {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	.btn-pix-pay {
		width: 100%;
		padding: 14px;
		border-radius: 8px;
		border: none;
		background: linear-gradient(135deg, #FF6A00 0%, #E65A00 100%);
		color: #FFFFFF;
		font-size: 14px;
		font-weight: 800;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		cursor: pointer;
		box-shadow: 0 4px 16px rgba(255, 106, 0, 0.35);
		transition: all 0.2s;
		font-family: inherit;
	}

	.btn-pix-pay:hover {
		background: linear-gradient(135deg, #E65A00 0%, #D84E00 100%);
		transform: translateY(-1px);
	}

	.key-activation-row {
		display: flex;
		gap: 8px;
		width: 100%;
	}

	.key-activation-row input {
		flex: 1;
		padding: 10px 14px;
		border-radius: 8px;
		border: 1px solid rgba(255, 255, 255, 0.15);
		background: #111113;
		color: #FFFFFF;
		font-size: 12px;
		font-family: inherit;
		outline: none;
	}

	.key-activation-row input:focus {
		border-color: #FF6A00;
	}

	.btn-activate-key {
		padding: 10px 16px;
		background: rgba(255, 255, 255, 0.08);
		border: 1px solid rgba(255, 255, 255, 0.15);
		color: #FFFFFF;
		border-radius: 8px;
		font-size: 12px;
		font-weight: 700;
		cursor: pointer;
		white-space: nowrap;
		font-family: inherit;
		transition: all 0.2s;
	}

	.btn-activate-key:hover {
		background: #FF6A00;
		border-color: #FF6A00;
	}

	.error-msg {
		color: #EF4444;
		font-size: 11px;
		margin: 4px 0 0 0;
	}

	.paywall-footer {
		margin-top: 28px;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
		font-size: 12px;
		color: #64748B;
	}

	.link-support {
		color: #FF6A00;
		text-decoration: none;
		font-weight: 600;
	}

	.link-support:hover {
		text-decoration: underline;
	}

	.btn-logout {
		background: none;
		border: none;
		color: #94A3B8;
		cursor: pointer;
		font-family: inherit;
		font-size: 12px;
		text-decoration: underline;
	}

	.bullet {
		color: #475569;
	}

	/* Modal PIX */
	.pix-modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		background: rgba(0, 0, 0, 0.75);
		backdrop-filter: blur(4px);
		z-index: 9999;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.pix-modal-content {
		background: #18181B;
		border: 1px solid rgba(255, 106, 0, 0.3);
		border-radius: 16px;
		width: 100%;
		max-width: 440px;
		padding: 28px;
		box-sizing: border-box;
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
	}

	.pix-modal-header {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 20px;
	}

	.pix-modal-header h3 {
		font-size: 16px;
		font-weight: 800;
		color: #FFFFFF;
		margin: 0;
	}

	.btn-close-modal {
		background: none;
		border: none;
		color: #94A3B8;
		font-size: 18px;
		cursor: pointer;
	}

	.qr-placeholder {
		background: #FFFFFF;
		padding: 12px;
		border-radius: 12px;
		display: inline-block;
		margin-bottom: 12px;
	}

	.qr-img {
		width: 180px;
		height: 180px;
		display: block;
	}

	.qr-instructions {
		font-size: 12px;
		color: #94A3B8;
		margin: 0 0 16px 0;
		line-height: 1.5;
	}

	.pix-code-box {
		width: 100%;
		text-align: left;
		margin-bottom: 20px;
	}

	.pix-code-box label {
		font-size: 11px;
		font-weight: 700;
		color: #94A3B8;
		text-transform: uppercase;
		margin-bottom: 6px;
		display: block;
	}

	.copy-input-row {
		display: flex;
		gap: 6px;
	}

	.copy-input-row input {
		flex: 1;
		padding: 8px 12px;
		background: #111113;
		border: 1px solid rgba(255, 255, 255, 0.1);
		color: #94A3B8;
		font-size: 11px;
		border-radius: 6px;
		outline: none;
	}

	.btn-copy {
		padding: 8px 14px;
		background: #FF6A00;
		color: #FFFFFF;
		border: none;
		border-radius: 6px;
		font-size: 12px;
		font-weight: 700;
		cursor: pointer;
		font-family: inherit;
	}

	.btn-confirm-pix {
		width: 100%;
		padding: 12px;
		background: #10B981;
		color: #FFFFFF;
		border: none;
		border-radius: 8px;
		font-weight: 800;
		font-size: 13px;
		cursor: pointer;
		font-family: inherit;
		transition: opacity 0.2s;
	}

	.btn-confirm-pix:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
</style>
